import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, PrimaryButton, Modal, Field, TextInput, Select, StatusPill, useFetch } from "../../components/crm/ui";
import { CustomerPicker } from "../../components/crm/CustomerPicker";
import { Plus, Trash2, Search, X, Link2, ExternalLink, Save, Edit3, Ban, FileSignature } from "lucide-react";

const QUOTE_STATUSES = [
  "draft",
  "sent",
  "customer_evaluation_wip",
  "verbally_confirmed_accepted",
  "reprocessing_required",
  "accepted",
  "rejected",
];

const QUOTE_STATUS_LABEL = {
  draft: "Draft",
  sent: "Sent",
  customer_evaluation_wip: "Customer Evaluation — WIP",
  verbally_confirmed_accepted: "Verbally Confirmed — Accepted",
  reprocessing_required: "Re-Processing Required",
  accepted: "Accepted",
  rejected: "Rejected",
};

const QUOTE_STATUS_TONE = {
  draft:                        { bg: "bg-slate-100",   fg: "text-slate-700",   dot: "bg-slate-500",  ring: "ring-slate-200" },
  sent:                         { bg: "bg-sky-100",     fg: "text-sky-800",     dot: "bg-sky-500",    ring: "ring-sky-200" },
  customer_evaluation_wip:      { bg: "bg-amber-100",   fg: "text-amber-800",   dot: "bg-amber-500",  ring: "ring-amber-200" },
  verbally_confirmed_accepted:  { bg: "bg-emerald-100", fg: "text-emerald-800", dot: "bg-emerald-500", ring: "ring-emerald-200" },
  reprocessing_required:        { bg: "bg-orange-100",  fg: "text-orange-800",  dot: "bg-orange-500", ring: "ring-orange-200" },
  accepted:                     { bg: "bg-emerald-200", fg: "text-emerald-900", dot: "bg-emerald-600", ring: "ring-emerald-300" },
  rejected:                     { bg: "bg-rose-100",    fg: "text-rose-800",    dot: "bg-rose-500",   ring: "ring-rose-200" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso; }
}

/**
 * Salesforce-inspired Quotations manager.
 * Routes:
 *   /crm/quotations                 → list
 *   /crm/quotations/new?opp=<id>    → new-quotation modal, opportunity prefilled
 *   /crm/quotations/:qid            → open-and-edit modal
 */
export default function Quotations() {
  const { qid } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const preselOpp = sp.get("opp");

  const { data: quotes, reload } = useFetch("/quotations");
  const { data: customers } = useFetch("/customers");
  const { data: materials } = useFetch("/materials");

  const [mode, setMode] = useState("closed");    // "closed" | "create" | "edit"
  const [current, setCurrent] = useState(null);   // draft or existing quotation
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const openBlank = useCallback(async () => {
    setSelectedCustomer(null);
    const draft = {
      opportunity_id: null,
      opportunity_meta: null,
      customer_id: "",
      lines: [],
      discount: 0,
      tax_percent: 18,
      notes: "",
      status: "draft",
    };
    if (preselOpp) {
      try {
        const { data } = await api.get(`/opportunities/${preselOpp}`);
        draft.opportunity_id = data.id;
        draft.customer_id = data.customer_id;
        draft.opportunity_meta = { id: data.id, code: data.code, name: data.name };
      } catch {/* silent */}
    }
    setCurrent(draft);
    setMode("create");
  }, [preselOpp]);

  const openExisting = useCallback(async (id) => {
    try {
      setSelectedCustomer(null);
      const { data } = await api.get(`/quotations/${id}`);
      let meta = null;
      if (data.opportunity_id) {
        try {
          const { data: o } = await api.get(`/opportunities/${data.opportunity_id}`);
          meta = { id: o.id, code: o.code, name: o.name };
        } catch {/* silent */}
      }
      setCurrent({ ...data, opportunity_meta: meta });
      setMode("edit");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      nav("/crm/quotations");
    }
  }, [nav]);

  useEffect(() => {
    if (qid) openExisting(qid);
    else if (preselOpp) openBlank();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qid, preselOpp]);

  // Hydrate the picker's selected-customer chip from customer_id — handles edit-mode
  // load (customers may arrive after the quotation fetch) and the "unlink opportunity"
  // case where customer_id persists but was never explicitly picked in this session.
  useEffect(() => {
    if (!current?.customer_id) return;
    if (selectedCustomer?.id === current.customer_id) return;
    const found = customers.find((c) => c.id === current.customer_id);
    if (found) setSelectedCustomer(found);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.customer_id, customers]);

  function close() {
    setMode("closed"); setCurrent(null);
    if (qid || preselOpp) nav("/crm/quotations");
  }

  function setLine(i, patch) {
    setCurrent((c) => {
      const next = c.lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l));
      if (patch.material_id !== undefined) {
        const m = materials.find((x) => x.id === patch.material_id);
        if (m) { next[i].material_name = m.name; next[i].unit_price = m.unit_price; }
      }
      next[i].total = (Number(next[i].quantity) || 0) * (Number(next[i].unit_price) || 0);
      return { ...c, lines: next };
    });
  }
  const addLine = () => setCurrent((c) => ({ ...c, lines: [...c.lines, { material_id: "", material_name: "", quantity: 1, unit_price: 0, total: 0 }] }));
  const removeLine = (i) => setCurrent((c) => ({ ...c, lines: c.lines.filter((_, idx) => idx !== i) }));

  const totals = useMemo(() => {
    if (!current) return { subtotal: 0, taxAmt: 0, grand: 0 };
    const subtotal = current.lines.reduce((s, l) => s + (l.total || 0), 0) - Number(current.discount || 0);
    const taxAmt = subtotal * (Number(current.tax_percent || 0) / 100);
    return { subtotal, taxAmt, grand: subtotal + taxAmt };
  }, [current]);

  async function save() {
    if (!current || saving) return;
    if (!current.customer_id) { toast.error("Customer required"); return; }
    if (current.lines.length === 0) { toast.error("Add at least one line item"); return; }
    setSaving(true);
    try {
      const payload = {
        opportunity_id: current.opportunity_id || null,
        customer_id: current.customer_id,
        lines: current.lines.map((l) => ({
          material_id: l.material_id, material_name: l.material_name,
          quantity: Number(l.quantity), unit_price: Number(l.unit_price),
          total: Number(l.quantity) * Number(l.unit_price),
        })),
        discount: Number(current.discount || 0),
        tax_percent: Number(current.tax_percent || 0),
        notes: current.notes || "",
      };
      if (mode === "edit") {
        await api.put(`/quotations/${current.id}`, { ...payload, status: current.status });
        toast.success("Quotation updated");
      } else {
        await api.post("/quotations", payload);
        toast.success("Quotation created");
      }
      close(); reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  }

  async function remove(q) {
    if (!window.confirm(`Delete ${q.quotation_number}?`)) return;
    await api.delete(`/quotations/${q.id}`); toast.success("Deleted"); reload();
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return quotes;
    return quotes.filter((x) => {
      const c = customers.find((c) => c.id === x.customer_id);
      const hay = [x.quotation_number, c?.customer_name, x.status].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [quotes, search, customers]);

  return (
    <div data-testid="quotations-page">
      <PageHeader
        overline="Sales · Salesforce-style"
        title="Quotations"
        action={<PrimaryButton onClick={openBlank} testid="create-quote-btn"><Plus className="w-4 h-4" /> New Quotation</PrimaryButton>}
      />

      <div className="bg-white border border-[#DDE5D8] mb-4">
        <div className="px-3 py-2 border-b border-[#DDE5D8] flex items-center gap-2">
          <Search className="w-3.5 h-3.5 text-[#94A3B8]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by number, customer, status…"
            data-testid="quotes-search"
            className="flex-1 text-[12px] font-mono outline-none bg-transparent"
          />
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">
            <span className="text-[#0F1A12] font-semibold">{filtered.length}</span> / {quotes.length}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]" data-testid="quotes-table">
            <thead className="bg-[#F4F6F1] border-b border-[#DDE5D8]">
              <tr className="text-left font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">
                <th className="px-3 py-2">Number</th>
                <th className="px-3 py-2">Origin</th>
                <th className="px-3 py-2">Customer</th>
                <th className="px-3 py-2">Created</th>
                <th className="px-3 py-2 text-right">Subtotal</th>
                <th className="px-3 py-2 text-right">Tax</th>
                <th className="px-3 py-2 text-right">Total</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((q) => {
                const c = customers.find((c) => c.id === q.customer_id);
                return (
                  <tr key={q.id} className="border-b border-[#EEF2EA] hover:bg-[#F4F6F1] cursor-pointer" onClick={() => nav(`/crm/quotations/${q.id}`)} data-testid={`quote-row-${q.id}`}>
                    <td className="px-3 py-2 font-mono text-[11px] font-semibold text-[#1F4E2C]">{q.quotation_number}</td>
                    <td className="px-3 py-2 font-mono text-[11px]">
                      {q.opportunity_code ? <span className="text-[#0F1A12]">→ {q.opportunity_code}</span>
                        : q.lead_code ? <span className="text-purple-800">← {q.lead_code}</span>
                        : <span className="text-[#94A3B8] italic">standalone</span>}
                    </td>
                    <td className="px-3 py-2">{c?.customer_name || "—"}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-[#475569]">{fmtDate(q.created_at)}</td>
                    <td className="px-3 py-2 text-right font-mono">₹{Number(q.subtotal || 0).toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2 text-right font-mono">₹{Number(q.tax_amount || 0).toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold">₹{Number(q.grand_total || 0).toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2">
                      {(() => {
                        const t = QUOTE_STATUS_TONE[q.status] || QUOTE_STATUS_TONE.draft;
                        return (
                          <span className={"inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider " + t.bg + " " + t.fg}>
                            <span className={"w-1.5 h-1.5 rounded-full " + t.dot} />
                            {QUOTE_STATUS_LABEL[q.status] || q.status}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => nav(`/crm/quotations/${q.id}`)} data-testid={`quote-edit-${q.id}`} className="p-1.5 hover:text-[#1F4E2C]" title="Open"><Edit3 className="w-3.5 h-3.5" /></button>
                      <button onClick={() => remove(q)} data-testid={`quote-del-${q.id}`} className="p-1.5 hover:text-red-600" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-[13px] text-[#475569]">No quotations found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {mode !== "closed" && current && (
        <QuotationModal
          mode={mode}
          current={current}
          setCurrent={setCurrent}
          totals={totals}
          customers={customers}
          materials={materials}
          onClose={close}
          onSave={save}
          saving={saving}
          onLineChange={setLine}
          onAddLine={addLine}
          onRemoveLine={removeLine}
          selectedCustomer={selectedCustomer}
          setSelectedCustomer={setSelectedCustomer}
        />
      )}
    </div>
  );
}

/** Modal — creates OR edits a quotation. Includes opportunity typeahead. */
function QuotationModal({ mode, current, setCurrent, totals, customers, materials, onClose, onSave, saving, onLineChange, onAddLine, onRemoveLine, selectedCustomer, setSelectedCustomer }) {
  const isEdit = mode === "edit";
  const tone = QUOTE_STATUS_TONE[current.status] || QUOTE_STATUS_TONE.draft;

  return (
    <Modal open onClose={onClose} title={isEdit ? `Edit Quotation · ${current.quotation_number}` : "New Quotation"} wide>
      <div className="space-y-4">
        {/* Enlarged status band — the quick-report at the opening of the quotation */}
        <div
          className={"flex items-center justify-between border-2 px-4 py-3 " + tone.bg + " " + tone.fg + " border-current/30"}
          data-testid="quote-status-band"
        >
          <div className="flex items-center gap-3">
            <span className={"w-3 h-3 rounded-full " + tone.dot + " ring-4 " + tone.ring} />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] opacity-80">Quotation Status</div>
              <div className="font-display text-lg font-bold leading-tight" data-testid="quote-status-band-label">
                {QUOTE_STATUS_LABEL[current.status] || current.status}
              </div>
            </div>
            {isEdit && current.grand_total != null && (
              <div className="ml-4 pl-4 border-l border-current/25">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] opacity-80">Grand Total</div>
                <div className="font-display text-lg font-bold leading-tight">₹{Number(current.grand_total || 0).toLocaleString("en-IN")}</div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <label className="font-mono text-[10px] uppercase tracking-widest opacity-70">Update to</label>
            <select
              value={current.status}
              onChange={(e) => setCurrent((c) => ({ ...c, status: e.target.value }))}
              data-testid="quote-status-select"
              className={"border border-current/30 bg-white/70 px-2.5 py-1.5 text-[12px] font-mono font-semibold outline-none cursor-pointer " + tone.fg}
            >
              {QUOTE_STATUSES.map((s) => (
                <option key={s} value={s}>{QUOTE_STATUS_LABEL[s]}</option>
              ))}
            </select>
          </div>
        </div>

        {current.lead_id && current.lead_code && (
          <Link
            to="/crm/leads"
            data-testid="quote-lead-backlink"
            className="border border-purple-200 bg-purple-50 px-3 py-2 flex items-center gap-2 text-[12.5px] hover:bg-purple-100 transition-colors"
            title="Originating Product Enquiry lead"
          >
            <FileSignature className="w-3.5 h-3.5 text-purple-700" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-purple-700">From Lead</span>
            <span className="font-mono text-[12px] font-semibold text-purple-900">{current.lead_code}</span>
          </Link>
        )}

        <OppLinkRow current={current} setCurrent={setCurrent} customers={customers} />

        {!current.opportunity_id && (
          <Field label="Customer *">
            <CustomerPicker
              selected={selectedCustomer}
              onSelect={(c) => { setSelectedCustomer(c); setCurrent((cur) => ({ ...cur, customer_id: c.id })); }}
              placeholder="Search customer by name, phone or email…"
              testid="quote-customer"
              required
            />
          </Field>
        )}

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">Line Items</div>
            <button type="button" onClick={onAddLine} data-testid="quote-add-line" className="text-[10px] font-mono uppercase tracking-widest text-[#1F4E2C] hover:underline">+ Add line</button>
          </div>
          <div className="space-y-1.5">
            {current.lines.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`quote-line-${i}`}>
                <select
                  value={l.material_id}
                  onChange={(e) => onLineChange(i, { material_id: e.target.value })}
                  data-testid={`quote-line-material-${i}`}
                  className="col-span-5 border border-[#DDE5D8] px-2 py-1.5 text-[12px] font-mono"
                >
                  <option value="">— Material —</option>
                  {materials.map((m) => <option key={m.id} value={m.id}>{m.code} · {m.name}</option>)}
                </select>
                <input type="number" min="1" value={l.quantity} onChange={(e) => onLineChange(i, { quantity: e.target.value })} data-testid={`quote-line-qty-${i}`} className="col-span-2 border border-[#DDE5D8] px-2 py-1.5 text-[12px] font-mono" placeholder="Qty" />
                <input type="number" value={l.unit_price} onChange={(e) => onLineChange(i, { unit_price: e.target.value })} data-testid={`quote-line-price-${i}`} className="col-span-2 border border-[#DDE5D8] px-2 py-1.5 text-[12px] font-mono" placeholder="Price" />
                <div className="col-span-2 text-right font-mono text-[12px]">₹{Number(l.total || 0).toLocaleString("en-IN")}</div>
                <button type="button" onClick={() => onRemoveLine(i)} data-testid={`quote-line-remove-${i}`} className="col-span-1 text-red-600 hover:text-red-800"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            {current.lines.length === 0 && (
              <div className="text-[11px] text-[#94A3B8] font-mono py-2 text-center border border-dashed border-[#DDE5D8]">Click "Add line" to start</div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Discount (₹)"><TextInput type="number" value={current.discount} onChange={(e) => setCurrent((c) => ({ ...c, discount: e.target.value }))} testid="quote-discount" /></Field>
          <Field label="Tax %"><TextInput type="number" value={current.tax_percent} onChange={(e) => setCurrent((c) => ({ ...c, tax_percent: e.target.value }))} testid="quote-tax" /></Field>
        </div>

        <Field label="Notes"><TextInput value={current.notes || ""} onChange={(e) => setCurrent((c) => ({ ...c, notes: e.target.value }))} testid="quote-notes" /></Field>

        <div className="bg-[#F4F6F1] p-3 font-mono text-[12px] space-y-1">
          <div className="flex justify-between"><span className="text-[#475569]">Subtotal (after discount)</span><span>₹{Number(totals.subtotal || 0).toLocaleString("en-IN")}</span></div>
          <div className="flex justify-between"><span className="text-[#475569]">Tax</span><span>₹{Number(totals.taxAmt || 0).toLocaleString("en-IN")}</span></div>
          <div className="flex justify-between font-display text-lg font-bold border-t border-[#DDE5D8] mt-1.5 pt-1.5"><span>Grand Total</span><span className="text-[#1F4E2C]" data-testid="quote-grand-total">₹{Number(totals.grand || 0).toLocaleString("en-IN")}</span></div>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} data-testid="quote-cancel" className="px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-[#475569] hover:text-[#0F1A12] inline-flex items-center gap-1">
            <Ban className="w-3 h-3" /> Cancel
          </button>
          <PrimaryButton onClick={onSave} disabled={saving} testid="quote-save-btn">
            <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Quotation"}
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
}

/** Opportunity link/typeahead row — shows the currently linked opp OR a search box. */
function OppLinkRow({ current, setCurrent, customers }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (!pickerOpen) return;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get(`/opportunities-search?q=${encodeURIComponent(q)}&limit=20`);
        setResults(data || []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [q, pickerOpen]);

  if (current.opportunity_id && current.opportunity_meta) {
    return (
      <div className="border border-emerald-200 bg-emerald-50 px-3 py-2 flex items-center justify-between" data-testid="quote-opp-linked">
        <div className="flex items-center gap-2 text-[12.5px]">
          <Link2 className="w-3.5 h-3.5 text-emerald-700" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-700">Linked Opportunity</span>
          <Link to={`/crm/opportunities/${current.opportunity_meta.id}`} data-testid="quote-opp-link" className="font-mono text-[12px] font-semibold text-[#1F4E2C] hover:underline flex items-center gap-1">
            {current.opportunity_meta.code || current.opportunity_meta.id.slice(0, 8)}
            <ExternalLink className="w-3 h-3" />
          </Link>
          <span className="text-[#475569]">·</span>
          <span className="text-[#0F1A12]">{current.opportunity_meta.name}</span>
        </div>
        <button
          type="button"
          onClick={() => setCurrent((c) => ({ ...c, opportunity_id: null, opportunity_meta: null }))}
          data-testid="quote-opp-unlink"
          className="p-1 text-[#475569] hover:text-red-600"
          title="Unlink (make standalone)"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">Opportunity</div>
        <div className="text-[10px] font-mono text-[#94A3B8]">
          Optional · leave blank for a <span className="italic">standalone</span> quotation
        </div>
      </div>
      {!pickerOpen ? (
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          data-testid="quote-opp-picker-open"
          className="w-full text-left border border-dashed border-[#DDE5D8] hover:border-[#1F4E2C] px-3 py-2 font-mono text-[11px] text-[#475569] hover:text-[#1F4E2C] flex items-center gap-2"
        >
          <Search className="w-3.5 h-3.5" /> Search & link an opportunity…
        </button>
      ) : (
        <div className="border border-[#DDE5D8] bg-white" data-testid="quote-opp-picker">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-[#DDE5D8]">
            <Search className="w-3.5 h-3.5 text-[#94A3B8]" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Type code, name or customer…"
              data-testid="quote-opp-picker-input"
              className="flex-1 text-[12.5px] font-mono outline-none"
            />
            <button type="button" onClick={() => setPickerOpen(false)} className="text-[#475569] hover:text-[#0F1A12]"><X className="w-3.5 h-3.5" /></button>
          </div>
          <div className="max-h-56 overflow-y-auto">
            {searching && <div className="text-[11px] text-[#475569] font-mono px-3 py-2">Searching…</div>}
            {!searching && results.length === 0 && <div className="text-[11px] text-[#94A3B8] font-mono px-3 py-2">No matches</div>}
            {!searching && results.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => {
                  setCurrent((c) => ({
                    ...c,
                    opportunity_id: o.id,
                    opportunity_meta: { id: o.id, code: o.code, name: o.name },
                    customer_id: o.customer_id,
                  }));
                  setPickerOpen(false); setQ("");
                }}
                data-testid={`quote-opp-picker-row-${o.id}`}
                className="w-full text-left px-3 py-2 border-b border-[#F4F6F1] hover:bg-[#F4F6F1] flex items-center gap-2 text-[12px]"
              >
                <span className="font-mono text-[11px] font-semibold text-[#1F4E2C] w-32">{o.code || o.id.slice(0, 8)}</span>
                <span className="flex-1 truncate text-[#0F1A12]">{o.name}</span>
                <span className="text-[10px] text-[#475569] font-mono truncate">{o.customer_name || "—"}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
